import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import chroma from "chroma-js";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

const FILTERS = [
  { id: "all", label: "All types" },
  { id: "animal", label: "Animal rescue" },
  { id: "medical", label: "Medical transport" },
  { id: "veteran", label: "Veteran support" },
  { id: "conservation", label: "Conservation" },
  { id: "other", label: "Other" },
];

function OperatingStatesPanel({ stateCodes, coverageByFilter }) {
  const [filter, setFilter] = useState("all");

  const handleFilter = (event, next) => {
    if (next !== null) setFilter(next);
  };

  const scale = useMemo(
    () =>
      chroma
        .scale([colors.grey[200], colors.info.main, colors.primary.main])
        .mode("lab"),
    []
  );

  const values = coverageByFilter[filter] || coverageByFilter.all;

  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: "1.25rem",
        border: "none",
        boxShadow: "0 4px 24px rgba(52, 71, 103, 0.07)",
      }}
    >
      <SoftBox p={{ xs: 3, md: 3.5 }}>
        <SoftTypography variant="h6" fontWeight="bold" mb={1} sx={{ lineHeight: 1.35 }}>
          Nonprofit operating coverage
        </SoftTypography>
        <SoftTypography variant="caption" color="text" display="block" mb={2.5} sx={{ lineHeight: 1.55 }}>
          States where nonprofits in this category are active (sample data)
        </SoftTypography>
        <ToggleButtonGroup
          exclusive
          value={filter}
          onChange={handleFilter}
          size="small"
          sx={{
            flexWrap: "wrap",
            gap: 0.75,
            mb: 2,
            "& .MuiToggleButton-root": {
              borderRadius: "2rem !important",
              px: 1.5,
              py: 0.35,
              textTransform: "none",
              fontSize: "0.75rem",
              border: `1px solid ${rgba(colors.dark.main, 0.08)} !important`,
            },
          }}
        >
          {FILTERS.map((f) => (
            <ToggleButton key={f.id} value={f.id}>
              {f.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <SoftBox
          display="grid"
          gridTemplateColumns="repeat(auto-fill, minmax(2.35rem, 1fr))"
          gap={0.75}
        >
          {stateCodes.map((code, i) => {
            const t = values[i] ?? 0;
            const bg = scale(t).hex();
            const fg = chroma.contrast(bg, "white") > 4.2 ? "#ffffff" : colors.dark.main;
            return (
              <SoftBox
                key={code}
                title={`${code}: ${Math.round(t * 100)}% relative coverage`}
                sx={{
                  aspectRatio: "1",
                  borderRadius: "0.35rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: bg,
                  color: fg,
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.15)",
                }}
              >
                {code}
              </SoftBox>
            );
          })}
        </SoftBox>
        <SoftBox display="flex" alignItems="center" gap={1.5} mt={2} flexWrap="wrap">
          <SoftTypography variant="caption" color="text">
            Lower
          </SoftTypography>
          <SoftBox
            height="0.5rem"
            flex={1}
            minWidth={120}
            borderRadius="1rem"
            sx={{
              background: `linear-gradient(90deg, ${colors.grey[200]} 0%, ${colors.info.main} 50%, ${colors.primary.main} 100%)`,
            }}
          />
          <SoftTypography variant="caption" color="text">
            Higher
          </SoftTypography>
        </SoftBox>
      </SoftBox>
    </Card>
  );
}

OperatingStatesPanel.propTypes = {
  stateCodes: PropTypes.arrayOf(PropTypes.string).isRequired,
  coverageByFilter: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
};

export default OperatingStatesPanel;
